import { Link } from "react-router-dom";
import styles from "./PageTitleHeader.module.css";

/** Use normal Georgian letters; capitalize the first letter for other scripts. */
function capitalize(str) {
  if (str == null || typeof str !== "string" || str.length === 0) return str;
  const normalized = str.replace(/[\u1C90-\u1CBF]/g, (letter) => letter.toLowerCase());
  if (/[\u10D0-\u10FF]/.test(normalized)) return normalized;
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

/**
 * Standard page title header: single h1 per page, optional breadcrumb, optional actions.
 * Use this on every page for consistent structure and accessibility.
 * Georgian titles and breadcrumb labels use normal letters without capitalization.
 *
 * @param {string} title - Main page title (rendered as h1)
 * @param {Array<{ label: string, to?: string }>} [breadcrumb] - Optional breadcrumb items; last item is current page (no link)
 * @param {React.ReactNode} [actions] - Optional node (e.g. buttons) to show on the right
 * @param {React.ReactNode} [titleAccessory] - Optional node beside the title (e.g. badge)
 */
export function PageTitleHeader({ title, breadcrumb, actions, titleAccessory }) {
  if (title == null || title === "") return null;

  const hasBreadcrumb = Array.isArray(breadcrumb) && breadcrumb.length > 0;

  return (
    <div className={styles.wrapper}>
      <div className={styles.titleBlock}>
        <div className={styles.titleHeadingRow}>
          {hasBreadcrumb ? (
            <nav className={styles.breadcrumb} aria-label="Breadcrumb">
              <ol className={styles.breadcrumbList}>
                {breadcrumb.map((item, i) => {
                  const isLast = i === breadcrumb.length - 1;
                  const label = capitalize(item.label);
                  return (
                    <li
                      key={i}
                      className={styles.breadcrumbItem}
                      {...(isLast ? { "aria-current": "page" } : {})}
                    >
                      {item.to != null && !isLast ? (
                        <Link className={styles.breadcrumbLink} to={item.to}>
                          {label}
                        </Link>
                      ) : isLast ? (
                        <h1 className={styles.pageTitle}>{label}</h1>
                      ) : (
                        <span>{label}</span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </nav>
          ) : (
            <h1 className={styles.pageTitle}>{capitalize(title)}</h1>
          )}
          {titleAccessory != null ? (
            <div className={styles.titleAccessory}>{titleAccessory}</div>
          ) : null}
        </div>
      </div>
      {actions != null ? <div className={styles.actions}>{actions}</div> : null}
    </div>
  );
}
